/**
 * Take-profit / stop-loss entered as ROE% instead of a price.
 *
 * ROE is the return on the margin the position posts, so for an entry price E
 * at leverage L a move of the price by a fraction m is an ROE of m × L:
 *
 *     long  TP  P = E × (1 + r / (100 L))      long  SL  P = E × (1 − r / (100 L))
 *     short TP  P = E × (1 − r / (100 L))      short SL  P = E × (1 + r / (100 L))
 *
 * `r` is always entered as a positive number — "take profit at +50%", "stop at
 * −25%" is typed as 25 — which is how Bybit and Hyperliquid present it.
 *
 * Fees are not modelled: the figure is the price move, not the net PnL.
 *
 * The trigger lands on the price grid rounded TOWARD the entry, so the order
 * never promises more than the trader asked for: a take-profit gains at most
 * the ROE typed, a stop-loss loses at most the ROE typed.
 */
import { Dec, dec } from "./decimal";

export type TpslLeg = "tp" | "sl";

export type RoeIssue =
  /** Not a positive ROE, entry or leverage. */
  | "invalid"
  /** A stop that far away is at or below a price of zero — past liquidation. */
  | "beyond_zero"
  /** The grid is coarser than the move: the trigger would sit on the entry. */
  | "at_entry";

export interface RoeTrigger {
  price?: Dec;
  issue?: RoeIssue;
}

const positive = (value: Dec | number | null | undefined): Dec | null => {
  if (value === null || value === undefined) return null;
  const parsed = dec.parse(value);
  return parsed !== null && dec.gt(parsed, "0") ? parsed : null;
};

/** Whether this leg sits above the entry: a long's TP, a short's SL. */
const above = (side: "buy" | "sell", leg: TpslLeg): boolean =>
  (side === "buy") === (leg === "tp");

export const triggerFromRoe = ({
  side,
  leg,
  entry,
  leverage,
  roePct,
  priceStep,
}: {
  side: "buy" | "sell";
  leg: TpslLeg;
  entry: Dec | number | null | undefined;
  leverage: Dec | number | null | undefined;
  roePct: Dec | number | null | undefined;
  priceStep: Dec | number;
}): RoeTrigger => {
  const e = positive(entry);
  const l = positive(leverage);
  const r = positive(roePct);
  if (e === null || l === null || r === null) return { issue: "invalid" };

  const move = dec.div(r, dec.mul("100", l), 40, "floor");
  if (move === null) return { issue: "invalid" };
  const up = above(side, leg);
  const factor = up ? dec.add("1", move) : dec.sub("1", move);
  if (!dec.gt(factor, "0")) return { issue: "beyond_zero" };

  const raw = dec.mul(e, factor);
  const step = dec.parse(priceStep);
  // Toward the entry: down when the trigger is above it, up when below.
  const onGrid =
    step !== null && dec.gt(step, "0")
      ? dec.toStep(raw, step, up ? "floor" : "ceil") ?? raw
      : raw;
  if (!dec.gt(onGrid, "0")) return { issue: "beyond_zero" };
  if (dec.eq(onGrid, e)) return { issue: "at_entry" };
  return { price: onGrid };
};

/**
 * The ROE% a trigger price stands for, floored to hundredths — used to carry a
 * typed price across when the trader switches the field to ROE%. `null` when
 * the price is on the wrong side of the entry for this leg, or not a price.
 */
export const roeFromTrigger = ({
  side,
  leg,
  entry,
  leverage,
  price,
}: {
  side: "buy" | "sell";
  leg: TpslLeg;
  entry: Dec | number | null | undefined;
  leverage: Dec | number | null | undefined;
  price: Dec | number | null | undefined;
}): Dec | null => {
  const e = positive(entry);
  const l = positive(leverage);
  const p = positive(price);
  if (e === null || l === null || p === null) return null;
  const distance = above(side, leg) ? dec.sub(p, e) : dec.sub(e, p);
  if (!dec.gt(distance, "0")) return null;
  const roe = dec.div(dec.mul(dec.mul(distance, l), "100"), e, 2, "floor");
  return roe !== null && dec.gt(roe, "0") ? roe : null;
};
